import React, { useEffect, useState } from "react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import Select from "react-select";
import { toast } from "react-toastify";
import { useLazyGetEmployeeByCodeQuery } from "../../featureSlice/employeeApi";

const RegistrationForm = ({ initialData, onSubmit, close }) => {
  const [form, setForm] = useState({
    rfid_uid: "",
    rfid_registration_for: "employee",
    employee_code: "",
    rfid_owner_name: "",
    card_token_number: "",
    rfid_registration_for_subcategory: "",
  });

  const [getEmployeeByCode, { isFetching }] = useLazyGetEmployeeByCodeQuery();

  // 🔹 Edit case
  useEffect(() => {
    if (initialData) {
      setForm({
        rfid_uid: initialData.rfid_uid || "",
        rfid_registration_for: initialData.rfid_registration_for || "",
        employee_code: initialData.employee_code || "",
        rfid_owner_name: initialData.rfid_owner_name || "",
        card_token_number: initialData.card_token_number || "",
        rfid_registration_for_subcategory:
          initialData.rfid_registration_for_subcategory || "",
      });
    }
  }, [initialData]);

  // 🔹 NFC Scanning
  useEffect(() => {
    const startNFCScanning = async () => {
      if ("NDEFReader" in window) {
        try {
          const ndef = new window.NDEFReader();
          await ndef.scan();

          ndef.addEventListener("reading", ({ message, serialNumber }) => {
            if (serialNumber) {
              handleChange("rfid_uid", serialNumber);
            }
          });
        } catch (error) {
          console.log("NFC scanning failed:", error);
        }
      }
    };

    startNFCScanning();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "employee_code" && { rfid_owner_name: "" }),
    }));
  };

  const handleEmployeeBlur = async () => {
    if (!form.employee_code) return;

    try {
      const res = await getEmployeeByCode(form.employee_code).unwrap();

      const employee = res?.data?.[0];

      if (!employee) {
        toast.error("Employee not found ❌");
        return;
      }

      setForm((prev) => ({
        ...prev,
        employee_code: employee.employee_code,
        rfid_owner_name: employee.employee_name,
      }));

      toast.success("Employee verified ✅");
    } catch (error) {
      toast.error("Invalid employee code ❌");

      setForm((prev) => ({
        ...prev,
        rfid_owner_name: "",
      }));
    }
  };

  const handleSubmitClick = () => {
    const payload = {
      rfid_uid: form.rfid_uid,
      rfid_registration_for: form.rfid_registration_for,

      rfid_registration_for_subcategory:
        form.rfid_registration_for === "employee"
          ? form.rfid_registration_for_subcategory
          : null,

      employee_code:
        form.rfid_registration_for === "employee" ? form.employee_code : null,

      rfid_owner_name:
        form.rfid_registration_for === "employee" ? form.rfid_owner_name : null,

      card_token_number:
        form.rfid_registration_for === "visitor"
          ? form.card_token_number
          : null,
    };

    onSubmit(payload);
    close();
  };

  const resetForm = () => {
    setForm({
      rfid_uid: "",
      rfid_registration_for: "employee",
      employee_code: "",
      card_token_number: "",
    });
  };

  const registrationOptions = [
    { label: "Employee", value: "employee" },
    { label: "Visitor", value: "visitor" },
  ];

  const subCategoryOptions = [
    { label: "vendor", value: "vendor" },
    { label: "staff", value: "staff" },
    { label: "contractual", value: "contractual" },
    { label: "group-employee", value: "group-employee" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg space-y-4 lg:w-[28%] w-[65%] text-darkGray font-medium relative">
        <Button
          className="absolute top-2 right-2 text-xl cursor-pointer bg-white border-none hover:bg-gray-100"
          onClick={close}
        >
          ✕
        </Button>

        <div className="mb-3">
          <Label>Registration For</Label>

          <Select
            options={registrationOptions}
            value={registrationOptions.find(
              (opt) => opt.value === form.rfid_registration_for
            )}
            onChange={(selected) =>
              handleChange("rfid_registration_for", selected.value)
            }
            placeholder="Select type"
          />
        </div>

        <div className="mb-3">
          <Label>RFID UID</Label>
          <Input
            value={form.rfid_uid}
            onChange={(e) => handleChange("rfid_uid", e.target.value)}
            placeholder="Tap RFID card to scan automatically"
          />
        </div>

        {form.rfid_registration_for === "employee" && (
          <div className="mb-3">
            <Label>Sub Category</Label>

            <Select
              options={subCategoryOptions}
              value={subCategoryOptions.find(
                (opt) => opt.value === form.rfid_registration_for_subcategory
              )}
              onChange={(selected) =>
                handleChange(
                  "rfid_registration_for_subcategory",
                  selected.value
                )
              }
              placeholder="Select sub category"
            />
          </div>
        )}

        {form.rfid_registration_for === "employee" && (
          <div className="mb-3">
            <Label>Employee Code</Label>
            <Input
              value={form.employee_code}
              onChange={(e) => handleChange("employee_code", e.target.value)}
              onBlur={handleEmployeeBlur}
              placeholder="Enter employee code"
            />
          </div>
        )}

        <div className="relative">
          <Input
            value={form.rfid_owner_name}
            placeholder={isFetching ? "Fetching employee..." : "Employee name"}
            disabled
          />

          {isFetching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          )}
        </div>

        {form.rfid_registration_for === "visitor" && (
          <div className="mb-3">
            <Label>Card Token Number</Label>
            <Input
              value={form.card_token_number}
              onChange={(e) =>
                handleChange("card_token_number", e.target.value)
              }
            />
          </div>
        )}

        <div className="flex justify-between gap-4 pt-4">
          <Button variant="outline" onClick={resetForm}>
            Reset
          </Button>
          <Button onClick={handleSubmitClick} className="bg-primary text-white">
            {initialData ? "Update" : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
